import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import ProjectSelect from '../../components/projects-select/projects-select.component'
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import moment from 'moment';
import 'chart.js/auto';
import { Doughnut } from 'react-chartjs-2';
import { getOverviewData } from '../../redux/thunks/admin/overview';

import { OverviewPageContainer, OverviewHeader, OverviewCards, OverviewDougnutContainer } from './overview.styles';

const dateRanges = [
  'Today',
  'Last 7 Days',
  'Last 30 Days',
  'This Month',
  'Last Month',
];

const CardComponent = ({ title, value, subtitle, isLoading }) => {
  const getBorderColor = (title) => {
    switch (title.trim().toLowerCase()) {
      case 'sales':
        return '#4caf50';
      case 'credits':
        return '#f44336';
      case 'expenses':
        return '#ff9800';
      case 'profit':
        return '#2196f3';
      default:
        return '#061929';
    }
  };

  return <Card style={{
    borderRadius: '10px',
    borderTop: `5px solid ${getBorderColor(title)}`,
    boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
  }}>
    <Card.Body>
      <Card.Title>{title}</Card.Title>
      <Card.Text>
        {isLoading ? <Spinner animation="border" variant='primary' /> :
        <h2 style={{ whiteSpace: 'nowrap' }}>${value}</h2>}
      </Card.Text>
      {!isLoading ? <Card.Subtitle className="text-muted">{subtitle}</Card.Subtitle> : null}
    </Card.Body>
  </Card>;
};

const OverviewPage = () => {
  const dispatch = useDispatch();
  const [selectedDateRangeText, setSelectedDateRangeText] = useState('Select date range');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const {
    isLoading,
    income,
    credits,
    expenses,
    profit,
    hours
  } = useSelector((state) => state.entities.admin.overview);
  
  const onDateRangeChange = (start, end, label) => setSelectedDateRange({
    start, end, label
  });

  useEffect(() => {
    const queryParameters = {};

    if (selectedProjects.length > 0) {
      queryParameters.projectIds = selectedProjects.map((project) => project._id);
    }

    if (selectedDateRange) {
      queryParameters.startDate = selectedDateRange.start.format('YYYY-MM-DD');
      queryParameters.endDate = selectedDateRange.end.format('YYYY-MM-DD');
    }

    dispatch(getOverviewData(queryParameters));
  }, [selectedDateRange, selectedProjects]);

  const getDoughnutData = () => {
    const sets = [
      { label: 'Sales', data: income, color: '#4caf50' },
      { label: 'Credits', data: credits, color: '#f44336' },
      { label: 'Expenses', data: expenses.employees.current + expenses.other, color: '#ff9800' },
      { label: 'Profit', data: profit.current, color: '#2196f3' },
    ];

    const positiveSets = sets.filter((set) => set.data > 0);

    const data = {
      labels: positiveSets.map((set) => set.label),
      datasets: [{
        label: 'Overview',
        data: positiveSets.map((set) => set.data),
        backgroundColor: positiveSets.map((set) => set.color),
        hoverOffset: 4,
      }]
    };

    return data;
  };

  return (
    <OverviewPageContainer>
      <OverviewHeader>
        <h1>Overview</h1>
        <span style={{ display: 'flex', gap: '0.5rem' }}>
          <ProjectSelect onChange={setSelectedProjects} />
          <DateRangePicker
            initialSettings={{
              startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
              endDate: new Date(),
              ranges: {
                Today: [new Date(), new Date()],
                'Last 7 Days': [
                  new Date(new Date().setDate(new Date().getDate() - 7)),
                  new Date(),
                ],
                'Last 30 Days': [
                  new Date(new Date().setDate(new Date().getDate() - 30)),
                  new Date(),
                ],
                'This Month': [
                  moment().startOf('month').format('MM/DD/YYYY'),
                  moment().endOf('month').format('MM/DD/YYYY'),
                ],
                'Last Month': [
                  moment()
                    .subtract(1, 'month')
                    .startOf('month')
                    .format('MM/DD/YYYY'),
                  moment()
                    .subtract(1, 'month')
                    .endOf('month')
                    .format('MM/DD/YYYY'),
                ],
              },
            }}
            onCallback={(start, end, label) => {
              setSelectedDateRangeText(
                dateRanges.includes(label)
                  ? label
                  : `${start.format('MM/DD/YYYY')} - ${end.format('MM/DD/YYYY')}`
              );
              onDateRangeChange(start, end, label);
            }}
          >
            <Button variant="primary" style={{ whiteSpace: 'nowrap' }}>
              {selectedDateRangeText}
            </Button>
          </DateRangePicker>
        </span>
      </OverviewHeader>
      
      <OverviewCards>
        <CardComponent title="Sales" 
          value={Number(income).toFixed(2)} 
          subtitle={`Paid for ${hours.paid} hours`}
          isLoading={isLoading} />
        <CardComponent title="Credits" 
          value={Number(credits).toFixed(2)} 
          subtitle={`Used for ${hours.credit} hours`}
          isLoading={isLoading} />
        <CardComponent title="Expenses"
          value={Number(Number(expenses.employees.current) + Number(expenses.other)).toFixed(2)}
          subtitle={`employees: $${Number(expenses.employees.current).toFixed(2)} | other: $${Number(expenses.other).toFixed(2)}`}
          isLoading={isLoading} />
        <CardComponent title="Profit" 
          value={Number(profit.current).toFixed(2)} 
          subtitle={`hours worked: ${hours.worked} | hours remaining: ${hours.remaining} | hours total: ${hours.total}`}
          isLoading={isLoading} />
      </OverviewCards>

      {/* <OverviewCards>
        <CardComponent title="Current Profit"
          value={Number(profit.current).toFixed(2)}
          subtitle={`hours worked: ${hours.worked} | hours remaining: ${hours.remaining}`}
          isLoading={isLoading} />
      </OverviewCards> */}

      <center>
        <OverviewDougnutContainer>
          <Doughnut
            data={getDoughnutData()}
            options={{
              plugins: {
                legend: {
                  display: false
                },
                tooltip: {
                  callbacks: {
                    label: (context) => {
                      const label = context.label;
                      const value = context.parsed;
                      return `${label}: $${value}`;
                    },
                  },
                  displayColors: false,
                },
              },
            }}
          />
        </OverviewDougnutContainer>
      </center>
    </OverviewPageContainer>
  );
};

export default OverviewPage;
